<script>
export default {
    name: 'BaseDrawer',
};
</script>

<script setup>
import { computed, ref, watch } from 'vue';
import {
    enter as chatEnter,
    leave as chatLeave,
} from '@/assets/util/hubspot-chat-position.js';
import { SIZE_WIDTH } from '@/components/base-components/components/drawer/constants.ts';
import { baseDrawerRegistry } from '@/components/base-components/components/drawer/base-drawer-registry.ts';

const props = defineProps({
    value: {
        type: Boolean,
        required: true,
    },
    size: {
        type: String,
        default: 'xl',
        validator(value) {
            return ['xs', 'sm', 'md', 'lg', 'xl', '2xl'].includes(value);
        },
    },
    render: {
        type: String,
        default: 'default',
        validator: value => ['default', 'lazy', 'always'].includes(value),
    },
    wrapperClass: {
        type: String,
    },
    contentClass: {
        type: String,
    },
    title: {
        type: String,
    },
    mode: {
        type: String,
        default: 'overlay',
        validator: value => ['overlay', 'persistent'].includes(value),
    },
    closableOverlay: {
        type: Boolean,
    },
});

const emit = defineEmits({
    input: null,
    close: null,
    closed: null,
    enter: null,
    leave: null,
});

const isRendered = ref(props.value);

const model = computed({
    get: () => props.value,
    set: (value) => {
        emit('input', value);

        if (!value) emit('close');
    },
});

const width = computed(() => SIZE_WIDTH[props.size]);

const close = () => {
    model.value = false;
};

const { zIndex, show } = baseDrawerRegistry(
    () => props.value,
    isRendered,
    () => props.mode,
    () => props.size,
    close,
);

watch([() => props.value, show], ([value, visible]) => {
    if (!value && !visible) return;

    isRendered.value = true;
}, { immediate: true });

function onClosed() {
    isRendered.value = false;

    emit('closed');
}

const clickOverlay = () => {
    if (!props.closableOverlay) return;

    close();
};

const isOverlay = computed(() => props.mode === 'overlay');
const isPersistent = computed(() => props.mode === 'persistent');

// TODO: Move outside and refactor usages?
const enter = el => chatEnter(el);
const leave = el => chatLeave(el);
</script>

<template>
    <div
        class="fixed inset-0 overflow-hidden"
        :class="[
            {
                'right-0': !isOverlay,
                'pointer-events-none': !show || isPersistent
            },
        ]"
        :style="{
            'z-index': zIndex,
        }"
    >
        <div
            class="absolute inset-0 h-screen w-screen bg-black transition-opacity duration-500 ease-in-out zi-max"
            :class="show && isOverlay ? 'opacity-40' : 'opacity-0 pointer-events-none'"
            @click="clickOverlay"
        />

        <transition
            enter-active-class="transition duration-500 ease-in-out"
            leave-active-class="transition duration-500 ease-in-out"
            leave-to-class="translate-x-full"
            enter-class="translate-x-full"
            @enter="enter"
            @leave="leave"
            @after-leave="onClosed()"
        >
            <div
                v-show="isRendered && show"
                class="absolute top-0 right-0 h-screen w-screen shadow-lg font-avenir zi-max pointer-events-auto"
                :class="[
                    wrapperClass || 'bg-white',
                ]"
                :style="{
                    width: width,
                }"
            >
                <div class="flex h-full flex-col">
                    <slot name="header" :close="close">
                        <div class="flex justify-end bg-gray-800 p-4 space-x-4">
                            <slot name="title">
                                <span
                                    v-if="title !== undefined"
                                    class="flex-1 self-center whitespace-nowrap text-xl font-semibold text-white"
                                >
                                    {{ title }}
                                </span>
                            </slot>

                            <div
                                class="cursor-pointer rounded-md p-2 text-white hover:bg-gray-700 active:bg-gray-600"
                                @click="close"
                            >
                                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                     stroke="currentColor"
                                     aria-hidden="true"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>
                        </div>
                    </slot>

                    <slot name="content">
                        <base-scroll
                            class="flex-1 py-4"
                            wrap-class="overflow-x-hidden"
                            :view-class="contentClass"
                        >
                            <slot />
                        </base-scroll>
                    </slot>

                    <slot name="footer" />
                </div>
            </div>
        </transition>
    </div>
</template>
